import React, { useState, useEffect, useMemo } from "react"
import classnames from "classnames"
import * as uuid from "uuid"
import * as httpx from "httpx"
import * as authzc from "authzcached"
import * as layouts from "layouts"
import * as sessions from "sessions"
import * as brands from "brands"
import * as api from "brandguard/api"
import * as bglayouts from "brandguard/components/layouts"
import * as bgapprovals from "brandguard/display/approval"
import * as bgimages from "brandguard/display/brandguard/images"
import * as bgtext from "brandguard/display/brandguard/text"
import * as bgdocuments from "brandguard/display/brandguard/documents"
import * as bgvideos from "brandguard/display/brandguard/videos"
import * as unified from "brandguard/display/unified"
import * as authz from "authz"
import * as debugx from "x/debugx"
import * as icons from "icons"
import * as bgscores from "brandguard/display/scores"
import * as typography from "typography"
import * as timex from "timex"
import { FeedbackPanel, FeedbackHistory } from "brandguard/feedback"
import { sameMedia, singleEventType, CardScore, downloadUnified, defaultLimit, applyOverride, FilterOverride, UnifiedFilterButton } from "./unified.helpers"

const reviewableSearchers: unified.FanoutSearchers = {
	image: api.images.reviewable.search,
	text: api.text.reviewable.search,
	document: api.documents.reviewable.search,
	video: api.videos.reviewable.search,
}

export default function Display(): JSX.Element {
	const authzaccount = authzc.useCache((cached) => cached.meta)
	const permission = authzaccount.current
	const bearertoken = sessions.useToken()
	const account = sessions.useAccount()
	const brand = brands.caching.useCached()
	const mtoggle = layouts.modals.useToggle()
	const { selected, setSelected, types } = unified.useAssetTypesFilter()

	const [loading, setLoading] = useState(true)
	const [selectedItem, setSelectedItem] = useState<unified.UnifiedItem | undefined>(undefined)
	const [checkedItems, setCheckedItems] = useState<unified.UnifiedItem[]>([])
	const [feedbackOpen, setFeedbackOpen] = useState(false)
	const [undoItems, setUndoItems] = useState<unified.UnifiedItem[]>([])
	const [currentPage, setCurrentPage] = useState<api.DocumentPage | undefined>(undefined)

	const checkedMediaIds = useMemo(() => {
		const ids = new Set<string>()
		for (const c of checkedItems) if (c.data.media?.id) ids.add(c.data.media.id)
		return ids
	}, [checkedItems])

	const undoByMediaId = useMemo(() => {
		const m = new Map<string, unified.UnifiedItem>()
		for (const u of undoItems) if (u.data.media?.id) m.set(u.data.media.id, u)
		return m
	}, [undoItems])

	const _baseline: unified.FanoutRequests = React.useMemo(
		() => ({
			image: api.images.uploads.searches.zero({ offset: 0n, limit: defaultLimit, brand_id: brand.id, queues: [brand.id] }),
			text: api.text.uploads.searchZero({ offset: 0n, limit: defaultLimit, brand_id: brand.id, queues: [brand.id] }),
			document: api.documents.uploads.searches.zero({ offset: 0n, limit: defaultLimit, brand_id: brand.id, queues: [brand.id] }),
			video: api.videos.uploads.searches.zero({
				offset: 0n,
				limit: defaultLimit,
				brand_id: brand.id,
				queues: [brand.id],
				observation: [api.Prediction.INREVIEW],
			}),
		}),
		[brand.id],
	)

	const [override, setOverride] = useState<Partial<FilterOverride>>({})
	const [filterFocused, setFilterFocused] = useState(false)
	const _requests: unified.FanoutRequests = React.useMemo(
		() => ({
			image: applyOverride(_baseline.image, override),
			text: applyOverride(_baseline.text, override),
			document: applyOverride(_baseline.document, override),
			video: applyOverride(_baseline.video, override),
		}),
		[_baseline, override],
	)

	const [requests, pagekey, setPage] = unified.useFanoutPage(_requests, types)

	const [result, setResult] = useState<unified.FanoutResult>({ items: [] })

	useEffect(() => {
		if (brand.id === uuid.NIL) return
		setLoading(true)
		const retry = httpx.autoretry()
		const p = retry
			.wrap(() => unified.fanoutSearch(brand.id, types, requests, reviewableSearchers, bearertoken))
			.then((r) => {
				setResult(r)
				setLoading(false)
			})
			.catch(httpx.errors.cancellation(() => undefined))
			.catch((cause) => {
				console.error("unable to search unified review", cause)
				setLoading(false)
			})
		return p.cancel
	}, [brand.id, types.join(","), requests])

	useEffect(() => {
		if (selectedItem?.event_type !== "document") {
			setCurrentPage(undefined)
			return
		}
		setCurrentPage(selectedItem.data.pages?.[0])
	}, [selectedItem?.event_type === "document" ? selectedItem.data.media?.id : undefined])

	const onUpdate = (updated: unified.UnifiedItem): void => {
		setResult((prev) => {
			const items = prev.items
			const idx = items.findIndex((i) => sameMedia(i, updated))
			if (idx < 0) return prev
			const removed = timex.from.iso(updated.data.media?.tombstoned_at || timex.infinity().toISO()) < timex.local()
			if (removed) return { ...prev, items: items.filter((_, i) => i !== idx) }
			const next = [...items]
			next[idx] = updated
			return { ...prev, items: next }
		})
	}

	const replaceInPlace = (updated: unified.UnifiedItem): void => {
		setResult((prev) => ({
			...prev,
			items: prev.items.map((i) => (sameMedia(i, updated) ? updated : i)),
		}))
		if (sameMedia(updated, selectedItem)) setSelectedItem(updated)
	}

	const publish = async (item: unified.UnifiedItem, observation: api.Prediction): Promise<unified.UnifiedItem> => {
		const bearer = authzc.bearer(authzaccount)
		const next = await (async (): Promise<unified.UnifiedItem> => {
			switch (item.event_type) {
				case "image": {
					const d = item.data as api.ImageSearchResponseItem & { event: api.ImageEvent }
					const r = await api.images.reviewable.publish(
						d.event.id,
						d.event.brand_id,
						{ ...d, event: { ...d.event, observation } },
						bearer,
					)
					return { event_type: "image", data: r }
				}
				case "text": {
					const d = item.data as api.TextSearchResponseItem & { event: api.TextEvent }
					const r = await api.text.reviewable.publish(
						d.event.id,
						d.event.brand_id,
						{ ...d, event: { ...d.event, observation } },
						bearer,
					)
					return { event_type: "text", data: r }
				}
				case "document": {
					const d = item.data as api.DocumentSearchResponseItem & { event: api.DocumentEvent }
					const r = await api.documents.reviewable.publish(
						d.event.id,
						d.event.brand_id,
						{ ...d, event: { ...d.event, observation } },
						bearer,
					)
					return { event_type: "document", data: { ...r, pages: d.pages } }
				}
				case "video": {
					const d = item.data as api.VideoSearchResponseItem & { event: api.VideoEvent }
					const r = await api.videos.reviewable.publish(
						d.event.id,
						d.event.brand_id,
						{ ...d, event: { ...d.event, observation } },
						bearer,
					)
					return { event_type: "video", data: r }
				}
			}
		})()
		setUndoItems((u) => [...u, item])
		replaceInPlace(next)
		return next
	}

	const approve = (item: unified.UnifiedItem) => publish(item, api.Prediction.APPROVED)
	const reject = (item: unified.UnifiedItem) => publish(item, api.Prediction.REJECTED)

	const undo = async (current: unified.UnifiedItem, previously: unified.UnifiedItem): Promise<unknown> => {
		const bearer = authzc.bearer(authzaccount)
		const clearUndo = (): void =>
			setUndoItems((u) => u.filter((i) => i.data.media?.id !== previously.data.media?.id))
		switch (previously.event_type) {
			case "image": {
				const prev = previously.data as api.ImageSearchResponseItem & { event: api.ImageEvent }
				const cur = current.data as api.ImageSearchResponseItem & { event: api.ImageEvent }
				const resp = await api.images.uploads.patch(prev.event.brand_id, prev.event.id, prev, bearer)
				clearUndo()
				replaceInPlace({ event_type: "image", data: prev })
				return api.images.approvallog
					.find(cur.event.id, cur.event.brand_id, bearertoken)
					.then((r) => api.images.approvallog.destroy(r.event!.brand_id, r.event!.id, bearer).then(() => resp))
			}
			case "text": {
				const prev = previously.data as api.TextSearchResponseItem & { event: api.TextEvent }
				const cur = current.data as api.TextSearchResponseItem & { event: api.TextEvent }
				const resp = await api.text.uploads.patch(prev.event.id, prev.event.brand_id, prev, bearer)
				clearUndo()
				replaceInPlace({ event_type: "text", data: prev })
				return api.text.approvallog
					.find(cur.event.id, cur.event.brand_id, bearertoken)
					.then((r) => api.text.approvallog.destroy(r.event!.brand_id, r.event!.id, bearer).then(() => resp))
			}
			case "document": {
				const prev = previously.data as api.DocumentSearchResponseItem & { event: api.DocumentEvent }
				const cur = current.data as api.DocumentSearchResponseItem & { event: api.DocumentEvent }
				const resp = await api.documents.uploads.patch(prev.event.brand_id, prev.event.id, prev, bearer)
				clearUndo()
				replaceInPlace({ event_type: "document", data: prev })
				return api.documents.approvallog
					.find(cur.event.id, cur.event.brand_id, bearertoken)
					.then((r) => api.documents.approvallog.destroy(r.event!.brand_id, r.event!.id, bearer).then(() => resp))
			}
			case "video": {
				const prev = previously.data as api.VideoSearchResponseItem & { event: api.VideoEvent }
				const resp = await api.videos.uploads.patch(prev.event.brand_id, prev.event.id, prev, bearer)
				clearUndo()
				replaceInPlace({ event_type: "video", data: prev })
				return resp
			}
		}
	}

	useEffect(() => {
		if (!selectedItem?.data.event) return
		const event = selectedItem.data.event
		mtoggle(
			<layouts.Flex styled flexDirection="column">
				{renderPreview({
					item: selectedItem,
					currentPage,
					undoByMediaId,
					canUpload: permission.brandguard_upload,
					onClose: () => {
						mtoggle(undefined)
						setSelectedItem(undefined)
					},
					onPageChange: (_i, p) => setCurrentPage(p),
					approve,
					reject,
					undo,
					onFeedback: (
						<>
							<layouts.containers.flex py="10px">
								<FeedbackPanel eventType={selectedItem.event_type} eventIds={[event.id]} />
							</layouts.containers.flex>
							<layouts.containers.flex py="10px">
								<FeedbackHistory eventType={selectedItem.event_type} eventId={event.id} />
							</layouts.containers.flex>
						</>
					),
				})}
			</layouts.Flex>,
		).then(() => setSelectedItem(undefined))
	}, [selectedItem, currentPage, undoByMediaId])

	const allselected = result.items.length > 0 && checkedItems.length === result.items.length

	const handleSelectAll = (): void =>
		allselected ? setCheckedItems([]) : setCheckedItems(result.items)

	const feedbackType = singleEventType(checkedItems)
	const feedbackEventIds = checkedItems.filter((i) => i.data.event).map((i) => i.data.event!.id)

	const correctionEligible = checkedItems.filter((i) => i.event_type === "image" || i.event_type === "video")
	const correctionType = singleEventType(correctionEligible)

	return (
		<layouts.containers.flex flexDirection="column" overflow="hidden" flex="1">
			<layouts.containers.flex flexDirection="column-reverse" overflow="hidden" flex="1">
				<layouts.pagination.Cursor
					key={pagekey}
					my="auto"
					justifyContent="center"
					current={requests}
					advance={loading ? undefined : result.next}
					onChange={setPage}
				/>
				<layouts.loading.screen
					className="brandguard-working-area"
					loading={loading}
					flex="1"
					overflow="hidden"
					icon={<></>}
					flexDirection="row"
				>
					<layouts.containers.flex className="center-panel" flex="6" flexDirection="column">
						<layouts.containers.flex flexDirection="column" flex="1" minHeight="100px">
							<bglayouts.OverlaysContainer overflow="auto" background={layouts.theme.backgrounds.whitealpha80}>
								<layouts.containers.ResponsiveGrid gap={20} p="25px">
									{result.items.map((item) => (
										<UnifiedCard
											key={item.data.media?.id}
											item={item}
											active={sameMedia(item, selectedItem)}
											checked={checkedMediaIds.has(item.data.media?.id ?? "")}
											onCardClick={() => setSelectedItem(item)}
											onChecked={(state) =>
												setCheckedItems((prev) =>
													state ? [...prev, item] : prev.filter((c) => !sameMedia(c, item)),
												)
											}
											onChange={onUpdate}
											panel={
												<bgapprovals.status.default>
													<bgapprovals.status.Review
														item={item.data}
														previtemstate={undoByMediaId.get(item.data.media?.id ?? "")?.data}
														agree={(x) =>
															approve({ event_type: item.event_type, data: x } as unified.UnifiedItem).then((r) => r.data)
														}
														disagree={(x) =>
															reject({ event_type: item.event_type, data: x } as unified.UnifiedItem).then((r) => r.data)
														}
														undo={(c, p) =>
															undo(
																{ event_type: item.event_type, data: c } as unified.UnifiedItem,
																{ event_type: item.event_type, data: p } as unified.UnifiedItem,
															) as Promise<typeof c>
														}
														borderRadius="0 0 10px 10px"
													/>
												</bgapprovals.status.default>
											}
										/>
									))}
								</layouts.containers.ResponsiveGrid>
								<layouts.loading.pending loading={loading}>
									{result.items.length === 0 && <bgapprovals.layouts.ZeroState />}
								</layouts.loading.pending>
							</bglayouts.OverlaysContainer>
						</layouts.containers.flex>
					</layouts.containers.flex>
				</layouts.loading.screen>
				{feedbackOpen && feedbackType && feedbackEventIds.length > 0 && (
					<layouts.containers.flex mb="10px">
						<FeedbackPanel eventType={feedbackType} eventIds={feedbackEventIds} />
					</layouts.containers.flex>
				)}
				<layouts.containers.flex flexDirection="row" alignItems="center" className={classnames(bgapprovals.layouts.styledactions)}>
					<UnifiedFilterButton
						baseline={_baseline.image}
						current={_requests.image}
						focused={filterFocused}
						onToggle={() => setFilterFocused(!filterFocused)}
						onClear={() => setOverride({})}
						onChange={(next) => setOverride((prev) => ({ ...prev, ...next }))}
						showApprovedRejectedCheckboxes
						assetTypesSelected={selected}
						onAssetTypesChange={setSelected}
					/>
					<layouts.containers.flex className="actions" justifyContent="flex-end" flex="10">
						{permission.brandguard_review && (
							<bgapprovals.layouts.FeedbackToggle
								disabled={!feedbackType || feedbackEventIds.length === 0}
								onClick={() => setFeedbackOpen((v) => !v)}
							>
								{feedbackOpen ? "Hide Feedback" : "Give Feedback"}
							</bgapprovals.layouts.FeedbackToggle>
						)}
						<bgapprovals.layouts.SelectAll
							disabled={result.items.length === 0}
							cursor="pointer"
							className="select-all-action"
							onClick={handleSelectAll}
						>
							{allselected ? "Unselect All" : "Select All"}
						</bgapprovals.layouts.SelectAll>
						<authz.Protected enabled={permission.brandguard_upload && account.correction_enabled && correctionType === "image"}>
							<bgimages.GenerateCorrection
								items={correctionEligible.map((i) => i.data as api.ImageSearchResponseItem)}
								onCorrectionComplete={() => {
									setCheckedItems([])
									setResult((prev) => ({ ...prev }))
								}}
							/>
						</authz.Protected>
						<authz.Protected enabled={permission.brandguard_upload && account.correction_enabled && correctionType === "video"}>
							<bgvideos.GenerateCorrection
								items={correctionEligible.map((i) => i.data as api.VideoSearchResponseItem)}
								onCorrectionComplete={() => {
									setCheckedItems([])
									setResult((prev) => ({ ...prev }))
								}}
							/>
						</authz.Protected>
						<authz.Protected
							enabled={permission.brandguard_upload && checkedItems.length > 0}
							rejected={
								<layouts.containers.flex className="download-action">
									<icons.brandguard.download />
								</layouts.containers.flex>
							}
						>
							<layouts.containers.flex
								className="download-action"
								cursor="pointer"
								onClick={() =>
									downloadUnified({ items: checkedItems, accountID: authzaccount.current.id, bearertoken })
								}
							>
								<icons.brandguard.download />
							</layouts.containers.flex>
						</authz.Protected>
					</layouts.containers.flex>
				</layouts.containers.flex>
			</layouts.containers.flex>
		</layouts.containers.flex>
	)
}

interface unifiedCardProps {
	item: unified.UnifiedItem
	active: boolean
	checked: boolean
	panel: JSX.Element
	onCardClick(): void
	onChecked(state: boolean): void
	onChange(next: unified.UnifiedItem): void
}

function UnifiedCard(props: unifiedCardProps): JSX.Element {
	const { item, active, checked, panel, onCardClick, onChecked, onChange } = props
	const score = <CardScore item={item} />
	switch (item.event_type) {
		case "image":
			return (
				<bgapprovals.images.CardDisplay
					item={item.data}
					active={active}
					checked={checked}
					score={score}
					panel={panel}
					onCardClick={onCardClick}
					onChecked={onChecked}
					onChange={(v) => onChange({ event_type: "image", data: v })}
					find={api.images.uploads.find}
				/>
			)
		case "text":
			return (
				<bgapprovals.texts.CardDisplay
					item={item.data}
					active={active}
					checked={checked}
					score={score}
					panel={panel}
					onCardClick={onCardClick}
					onChecked={onChecked}
					onChange={(v) => onChange({ event_type: "text", data: v })}
					find={api.text.uploads.find}
				/>
			)
		case "document":
			return (
				<bgapprovals.documents.CardDisplay
					item={item.data}
					active={active}
					checked={checked}
					score={score}
					panel={panel}
					onCardClick={onCardClick}
					onChecked={onChecked}
					onChange={(v) => onChange({ event_type: "document", data: v })}
					find={api.documents.uploads.find}
				/>
			)
		case "video":
			return (
				<bgapprovals.videos.CardDisplay
					item={item.data}
					active={active}
					checked={checked}
					score={score}
					panel={panel}
					onCardClick={onCardClick}
					onChecked={onChecked}
					onChange={(v) => onChange({ event_type: "video", data: v })}
					find={api.videos.uploads.find}
				/>
			)
	}
}

interface previewProps {
	item: unified.UnifiedItem
	currentPage: api.DocumentPage | undefined
	undoByMediaId: Map<string, unified.UnifiedItem>
	canUpload: boolean
	onClose(): void
	onPageChange(index: number, page: api.DocumentPage | undefined): void
	approve(item: unified.UnifiedItem): Promise<unified.UnifiedItem>
	reject(item: unified.UnifiedItem): Promise<unified.UnifiedItem>
	undo(current: unified.UnifiedItem, previously: unified.UnifiedItem): Promise<unknown>
	onFeedback: JSX.Element
}

function renderPreview(props: previewProps): JSX.Element {
	const { item, currentPage, undoByMediaId, canUpload, onClose, onPageChange, approve, reject, undo, onFeedback } = props
	const previtem = undoByMediaId.get(item.data.media?.id ?? "")
	const scoresPanel = (() => {
		switch (item.event_type) {
			case "image":
				return <bgimages.Scores item={item.data} />
			case "text":
				return <bgtext.Scores item={item.data} />
			case "document":
				return <bgdocuments.Scores item={item.data} currentPage={currentPage} />
			case "video":
				return <bgvideos.Scores item={item.data} />
		}
	})()
	const debugPanel = (() => {
		switch (item.event_type) {
			case "image":
				return <bgimages.Debug item={item.data} />
			case "text":
				return <bgtext.Debug item={item.data} />
			case "document":
				return <bgdocuments.Debug item={item.data} />
			case "video":
				return (
					<layouts.containers.flex flexDirection="column" fontSize="12px">
						<layouts.containers.flex>ID: {item.data.event?.id}</layouts.containers.flex>
						<layouts.containers.flex>Confidence: {item.data.event?.confidence}</layouts.containers.flex>
						<layouts.containers.flex>Brand Voice: {item.data.event?.video_brand_voice}</layouts.containers.flex>
						<layouts.containers.flex>Violence: {item.data.event?.video_violence}</layouts.containers.flex>
						<layouts.containers.flex>Nudity: {item.data.event?.video_sexually_explicit}</layouts.containers.flex>
					</layouts.containers.flex>
				)
		}
	})()
	const multiActions = (() => {
		switch (item.event_type) {
			case "image":
				return <bgimages.MultiActions uploadItems={[item.data]} />
			case "text":
				return <bgtext.MultiActions uploadItems={[item.data]} />
			case "document":
				return <bgdocuments.MultiActions uploadItems={[item.data]} historicalItems={[]} />
			case "video":
				return <bgvideos.MultiActions uploadItems={[item.data]} />
		}
	})()
	const body = (
		<>
			<typography.h3 fontWeight="700" fontSize="18px" lineHeight="30px" color="#4D5E80">
				Asset Scores
			</typography.h3>
			{scoresPanel}
			<bgapprovals.status.default>
				<bgapprovals.status.Review
					item={item.data}
					previtemstate={previtem?.data}
					agree={(x) => approve({ event_type: item.event_type, data: x } as unified.UnifiedItem).then((r) => r.data)}
					disagree={(x) => reject({ event_type: item.event_type, data: x } as unified.UnifiedItem).then((r) => r.data)}
					undo={(c, p) =>
						undo(
							{ event_type: item.event_type, data: c } as unified.UnifiedItem,
							{ event_type: item.event_type, data: p } as unified.UnifiedItem,
						) as Promise<typeof c>
					}
					borderRadius="10px"
				/>
			</bgapprovals.status.default>
			{onFeedback}
			<authz.Protected enabled={debugx.alpha.enabled()}>
				<bgscores.layouts.accordion.Container title="Debug Info" textAlign="initial">
					{debugPanel}
				</bgscores.layouts.accordion.Container>
			</authz.Protected>
			<layouts.containers.flex className="actions" flex="1" alignItems="flex-end">
				<authz.Protected enabled={canUpload}>{multiActions}</authz.Protected>
			</layouts.containers.flex>
		</>
	)
	switch (item.event_type) {
		case "image":
			return (
				<bgapprovals.ImagePreview item={item.data} flex="1" onClose={onClose}>
					{body}
				</bgapprovals.ImagePreview>
			)
		case "text":
			return (
				<bgapprovals.TextPreview item={item.data} flex="1" onClose={onClose}>
					{body}
				</bgapprovals.TextPreview>
			)
		case "document":
			return (
				<bgapprovals.DocumentPreview item={item.data} flex="1" onClose={onClose} onPageChange={onPageChange}>
					{body}
				</bgapprovals.DocumentPreview>
			)
		case "video":
			return (
				<bgapprovals.VideoPreview item={item.data} flex="1" onClose={onClose}>
					{body}
				</bgapprovals.VideoPreview>
			)
	}
}
